import { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { usePlayer } from '@/context/PlayerContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { TrackCard } from '@/components/TrackCard';
import { 
  MusicNote, House, Disc, AddressBook, PaperPlaneTilt, Gear, SignOut, Plus, Upload, 
  SpotifyLogo, SoundcloudLogo, X, ChartLine, Envelope
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Sidebar = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const navItems = [
    { icon: House, label: 'Dashboard', path: '/dashboard' },
    { icon: Disc, label: 'I miei brani', path: '/dashboard/tracks' },
    { icon: AddressBook, label: 'Contatti', path: '/dashboard/contacts' },
    { icon: PaperPlaneTilt, label: 'Promozioni', path: '/dashboard/promotions' },
    { icon: Gear, label: 'Profilo', path: '/dashboard/profile' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <aside className="fixed left-0 top-0 h-full w-64 bg-[#0A0A0A] border-r border-white/10 flex flex-col z-30">
      {/* Logo */}
      <div className="p-6 border-b border-white/10">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-lg bg-[#CCFF00] flex items-center justify-center">
            <MusicNote size={24} weight="fill" className="text-black" />
          </div>
          <span className="font-bold text-xl">BeatPromoter</span>
        </Link>
      </div>

      {/* Nav */}
      <nav className="flex-1 p-4 space-y-1">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path || 
            (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`sidebar-link flex items-center gap-3 px-4 py-3 rounded-lg ${
                isActive ? 'active' : 'text-[#A1A1AA]'
              }`}
              data-testid={`sidebar-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon size={20} weight={isActive ? 'fill' : 'regular'} />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* User */}
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center">
            <MusicNote size={18} weight="fill" className="text-[#CCFF00]" />
          </div>
          <div className="min-w-0">
            <p className="font-medium text-white truncate">{user?.name}</p>
            <p className="text-xs text-[#A1A1AA] truncate">{user?.email}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 text-[#A1A1AA] hover:text-red-400 transition-colors text-sm"
          data-testid="sidebar-logout"
        >
          <SignOut size={18} />
          Esci
        </button>
      </div>
    </aside>
  );
};

// Dashboard Overview
const DashboardOverview = () => {
  const [stats, setStats] = useState(null);
  const [recentTracks, setRecentTracks] = useState([]);
  const { playTrack } = usePlayer();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, tracksRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/tracks`),
      ]);
      setStats(statsRes.data);
      setRecentTracks(tracksRes.data.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Dashboard</h1>
        <p className="text-[#A1A1AA]">Panoramica della tua attività</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Brani', value: stats?.tracks || 0, icon: Disc },
          { label: 'Ascolti totali', value: stats?.total_plays || 0, icon: ChartLine },
          { label: 'Contatti', value: stats?.contacts || 0, icon: AddressBook },
          { label: 'Email inviate', value: stats?.total_emails_sent || 0, icon: Envelope },
        ].map((stat) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="stat-card"
          >
            <div className="flex items-center justify-between mb-2">
              <stat.icon size={24} className="text-[#A1A1AA]" />
            </div>
            <div className="stat-value">{stat.value}</div>
            <div className="stat-label">{stat.label}</div>
          </motion.div>
        ))}
      </div>

      {/* Recent Tracks */}
      <div className="card rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">Brani recenti</h2>
          <Link to="/dashboard/tracks">
            <Button variant="ghost" className="text-[#CCFF00] hover:text-[#E5FF00]">
              Vedi tutti
            </Button>
          </Link>
        </div>
        {recentTracks.length > 0 ? (
          <div className="space-y-2">
            {recentTracks.map((track, index) => (
              <TrackCard key={track.id} track={track} index={index} onPlay={() => playTrack(track, recentTracks)} />
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <Disc size={48} className="text-[#333] mb-4" />
            <p className="text-[#A1A1AA]">Nessun brano ancora caricato</p>
            <Link to="/dashboard/tracks">
              <Button className="mt-4 bg-[#CCFF00] text-black hover:bg-[#E5FF00]">
                Carica il tuo primo brano
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

// Tracks Page
const TracksPage = () => {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadType, setUploadType] = useState('file');
  const [uploading, setUploading] = useState(false);
  const [genres, setGenres] = useState([]);
  const { playTrack } = usePlayer();

  const [newTrack, setNewTrack] = useState({
    title: '',
    genre: '',
    external_url: '',
    duration: '',
  });
  const [audioFile, setAudioFile] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tracksRes, genresRes] = await Promise.all([
        axios.get(`${API}/tracks`),
        axios.get(`${API}/genres`),
      ]);
      setTracks(tracksRes.data);
      setGenres(genresRes.data);
    } catch (error) {
      console.error('Failed to fetch tracks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTrack = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      // Create track
      const trackData = {
        title: newTrack.title,
        genre: newTrack.genre,
        external_url: uploadType === 'link' ? newTrack.external_url : null,
        duration: newTrack.duration ? parseInt(newTrack.duration) : null,
      };

      const response = await axios.post(`${API}/tracks`, trackData);
      const track = response.data;

      // Upload audio if file selected
      if (uploadType === 'file' && audioFile) {
        const formData = new FormData();
        formData.append('file', audioFile);
        await axios.post(`${API}/tracks/${track.id}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      toast.success('Brano creato con successo!');
      setDialogOpen(false);
      setNewTrack({ title: '', genre: '', external_url: '', duration: '' });
      setAudioFile(null);
      fetchData();
    } catch (error) {
      toast.error('Errore durante la creazione del brano');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteTrack = async (trackId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo brano?')) return;
    try {
      await axios.delete(`${API}/tracks/${trackId}`);
      toast.success('Brano eliminato');
      fetchData();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">I miei brani</h1>
          <p className="text-[#A1A1AA]">Gestisci i tuoi brani e album</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#CCFF00] text-black hover:bg-[#E5FF00] rounded-full" data-testid="add-track-btn">
              <Plus size={20} className="mr-2" />
              Nuovo brano
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#111] border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle>Aggiungi un nuovo brano</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTrack} className="space-y-4 mt-4">
              {/* Upload Type Toggle */}
              <div className="flex gap-2 p-1 bg-[#0A0A0A] rounded-lg">
                <button
                  type="button"
                  onClick={() => setUploadType('file')}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium flex items-center justify-center gap-2 transition-all ${
                    uploadType === 'file' ? 'bg-[#CCFF00] text-black' : 'text-[#A1A1AA]'
                  }`}
                >
                  <Upload size={16} />
                  Carica file
                </button>
                <button
                  type="button"
                  onClick={() => setUploadType('link')}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium flex items-center justify-center gap-2 transition-all ${
                    uploadType === 'link' ? 'bg-[#CCFF00] text-black' : 'text-[#A1A1AA]'
                  }`}
                >
                  <SpotifyLogo size={16} />
                  Link esterno
                </button>
              </div>

              <div className="space-y-2">
                <Label>Titolo</Label>
                <Input
                  value={newTrack.title}
                  onChange={(e) => setNewTrack({ ...newTrack, title: e.target.value })}
                  placeholder="Titolo del brano"
                  required
                  className="bg-[#0A0A0A] border-white/10"
                  data-testid="track-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Genere</Label>
                <Select value={newTrack.genre} onValueChange={(v) => setNewTrack({ ...newTrack, genre: v })}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/10" data-testid="track-genre-select">
                    <SelectValue placeholder="Seleziona genere" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111] border-white/10">
                    {genres.map((genre) => (
                      <SelectItem key={genre} value={genre}>{genre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {uploadType === 'file' ? (
                <div className="space-y-2">
                  <Label>File audio</Label>
                  <div 
                    className={`dropzone ${audioFile ? 'active' : ''}`}
                    onClick={() => document.getElementById('audio-input').click()}
                  >
                    {audioFile ? (
                      <div className="flex items-center gap-2">
                        <MusicNote size={24} className="text-[#CCFF00]" />
                        <span className="truncate">{audioFile.name}</span>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setAudioFile(null); }}
                          className="text-[#A1A1AA] hover:text-white"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <Upload size={32} className="text-[#A1A1AA] mb-2" />
                        <p className="text-[#A1A1AA]">Clicca o trascina un file audio</p>
                        <p className="text-xs text-[#666]">MP3, WAV, OGG, M4A, FLAC</p>
                      </>
                    )}
                  </div>
                  <input
                    id="audio-input"
                    type="file"
                    accept="audio/*"
                    className="hidden"
                    onChange={(e) => setAudioFile(e.target.files?.[0] || null)}
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Link</Label>
                  <Input
                    value={newTrack.external_url}
                    onChange={(e) => setNewTrack({ ...newTrack, external_url: e.target.value })}
                    placeholder="https://open.spotify.com/track/..."
                    className="bg-[#0A0A0A] border-white/10"
                    data-testid="track-link-input"
                  />
                  <div className="flex gap-2 text-xs text-[#A1A1AA]">
                    <SpotifyLogo size={14} /> Spotify
                    <SoundcloudLogo size={14} className="ml-2" /> SoundCloud
                  </div>
                </div>
              )}

              <Button
                type="submit"
                disabled={uploading}
                className="w-full bg-[#CCFF00] text-black hover:bg-[#E5FF00]"
                data-testid="save-track-btn"
              >
                {uploading ? 'Caricamento...' : 'Salva brano'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tracks List */}
      <div className="card rounded-xl p-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : tracks.length > 0 ? (
          <div className="space-y-2">
            {tracks.map((track, index) => (
              <div key={track.id} className="flex items-center gap-2">
                <div className="flex-1">
                  <TrackCard track={track} index={index} onPlay={() => playTrack(track, tracks)} />
                </div>
                <button
                  onClick={() => handleDeleteTrack(track.id)}
                  className="p-2 text-[#A1A1AA] hover:text-red-400 transition-colors"
                  data-testid={`delete-track-${track.id}`}
                >
                  <X size={18} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <Disc size={48} className="text-[#333] mb-4" />
            <p className="text-[#A1A1AA] mb-4">Nessun brano ancora caricato</p>
            <Button onClick={() => setDialogOpen(true)} className="bg-[#CCFF00] text-black hover:bg-[#E5FF00]">
              <Plus size={20} className="mr-2" />
              Carica il tuo primo brano
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// Contacts Page
const ContactsPage = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newContact, setNewContact] = useState({
    name: '',
    email: '',
    contact_type: 'radio',
    location: '',
    notes: '',
  });

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts`);
      setContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateContact = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/contacts`, newContact);
      toast.success('Contatto aggiunto!');
      setDialogOpen(false);
      setNewContact({ name: '', email: '', contact_type: 'radio', location: '', notes: '' });
      fetchContacts();
    } catch (error) {
      toast.error('Errore durante l\'aggiunta del contatto');
    }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await axios.delete(`${API}/contacts/${contactId}`);
      toast.success('Contatto eliminato');
      fetchContacts();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const contactTypes = [
    { value: 'radio', label: 'Radio' },
    { value: 'venue', label: 'Locale/Club' },
    { value: 'organizer', label: 'Organizzatore' },
    { value: 'label', label: 'Etichetta' },
  ];

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Contatti</h1>
          <p className="text-[#A1A1AA]">Gestisci i tuoi contatti per le promozioni</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#CCFF00] text-black hover:bg-[#E5FF00] rounded-full" data-testid="add-contact-btn">
              <Plus size={20} className="mr-2" />
              Nuovo contatto
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#111] border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle>Aggiungi un contatto</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateContact} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Nome</Label>
                <Input
                  value={newContact.name}
                  onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                  placeholder="Es. Radio XYZ"
                  required
                  className="bg-[#0A0A0A] border-white/10"
                  data-testid="contact-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newContact.email}
                  onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                  placeholder="email@esempio.com"
                  required
                  className="bg-[#0A0A0A] border-white/10"
                  data-testid="contact-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={newContact.contact_type} onValueChange={(v) => setNewContact({ ...newContact, contact_type: v })}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/10" data-testid="contact-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111] border-white/10">
                    {contactTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Città (opzionale)</Label>
                <Input
                  value={newContact.location}
                  onChange={(e) => setNewContact({ ...newContact, location: e.target.value })}
                  placeholder="Es. Milano"
                  className="bg-[#0A0A0A] border-white/10"
                />
              </div>
              <div className="space-y-2">
                <Label>Note (opzionale)</Label>
                <Textarea
                  value={newContact.notes}
                  onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })}
                  placeholder="Note aggiuntive..."
                  rows={2}
                  className="bg-[#0A0A0A] border-white/10 resize-none"
                />
              </div>
              <Button type="submit" className="w-full bg-[#CCFF00] text-black hover:bg-[#E5FF00]" data-testid="save-contact-btn">
                Salva contatto
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Contacts Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : contacts.length > 0 ? (
          contacts.map((contact, index) => (
            <motion.div
              key={contact.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="card rounded-xl p-4"
              data-testid={`contact-card-${contact.id}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-white">{contact.name}</h3>
                  <p className="text-sm text-[#A1A1AA]">{contact.email}</p>
                </div>
                <button
                  onClick={() => handleDeleteContact(contact.id)}
                  className="p-1 text-[#A1A1AA] hover:text-red-400 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <span className="badge badge-primary">
                  {contactTypes.find(t => t.value === contact.contact_type)?.label || contact.contact_type}
                </span>
                {contact.location && (
                  <span className="text-xs text-[#A1A1AA]">{contact.location}</span>
                )}
              </div>
            </motion.div>
          ))
        ) : (
          <div className="col-span-full card rounded-xl p-12">
            <div className="empty-state">
              <AddressBook size={48} className="text-[#333] mb-4" />
              <p className="text-[#A1A1AA] mb-4">Nessun contatto salvato</p>
              <Button onClick={() => setDialogOpen(true)} className="bg-[#CCFF00] text-black hover:bg-[#E5FF00]">
                <Plus size={20} className="mr-2" />
                Aggiungi il tuo primo contatto
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Promotions Page
const PromotionsPage = () => {
  const [tracks, setTracks] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  
  const [selectedTracks, setSelectedTracks] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tracksRes, contactsRes, promosRes] = await Promise.all([
        axios.get(`${API}/tracks`),
        axios.get(`${API}/contacts`),
        axios.get(`${API}/promotions`),
      ]);
      setTracks(tracksRes.data);
      setContacts(contactsRes.data);
      setPromotions(promosRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendPromotion = async () => {
    if (selectedTracks.length === 0) {
      toast.error('Seleziona almeno un brano');
      return;
    }
    if (selectedContacts.length === 0) {
      toast.error('Seleziona almeno un contatto');
      return;
    }
    if (!subject.trim()) {
      toast.error('Inserisci un oggetto');
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${API}/promotions`, {
        track_ids: selectedTracks,
        contact_ids: selectedContacts,
        subject,
        message,
      });
      
      toast.success(`Promozione inviata a ${response.data.emails_sent} contatti!`);
      setSelectedTracks([]);
      setSelectedContacts([]);
      setSubject('');
      setMessage('');
      fetchData();
    } catch (error) {
      toast.error('Errore durante l\'invio della promozione');
    } finally {
      setSending(false);
    }
  };

  const toggleTrack = (trackId) => {
    setSelectedTracks(prev => 
      prev.includes(trackId) 
        ? prev.filter(id => id !== trackId)
        : [...prev, trackId]
    );
  };

  const toggleContact = (contactId) => {
    setSelectedContacts(prev => 
      prev.includes(contactId) 
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Promozioni</h1>
        <p className="text-[#A1A1AA]">Invia i tuoi brani a radio, locali e organizzatori</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Create Promotion */}
        <div className="card rounded-xl p-6 space-y-6">
          <h2 className="text-xl font-semibold text-white">Nuova promozione</h2>

          {/* Select Tracks */}
          <div className="space-y-3">
            <Label className="text-white">Seleziona i brani</Label>
            {tracks.length > 0 ? (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {tracks.map((track) => (
                  <label
                    key={track.id}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedTracks.includes(track.id) ? 'bg-[#CCFF00]/10 border border-[#CCFF00]/30' : 'bg-[#0A0A0A] border border-transparent'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTracks.includes(track.id)}
                      onChange={() => toggleTrack(track.id)}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                      selectedTracks.includes(track.id) ? 'bg-[#CCFF00] border-[#CCFF00]' : 'border-white/30'
                    }`}>
                      {selectedTracks.includes(track.id) && (
                        <svg className="w-3 h-3 text-black" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <span className="text-white">{track.title}</span>
                    {track.genre && <span className="text-xs text-[#A1A1AA]">{track.genre}</span>}
                  </label>
                ))}
              </div>
            ) : (
              <p className="text-[#A1A1AA] text-sm">Nessun brano disponibile. <Link to="/dashboard/tracks" className="text-[#CCFF00]">Carica un brano</Link></p>
            )}
          </div>

          {/* Select Contacts */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-white">Seleziona i destinatari</Label>
              <button
                onClick={() => setSelectedContacts(contacts.map(c => c.id))}
                className="text-xs text-[#CCFF00] hover:underline"
              >
                Seleziona tutti
              </button>
            </div>
            {contacts.length > 0 ? (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {contacts.map((contact) => (
                  <label
                    key={contact.id}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedContacts.includes(contact.id) ? 'bg-[#CCFF00]/10 border border-[#CCFF00]/30' : 'bg-[#0A0A0A] border border-transparent'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedContacts.includes(contact.id)}
                      onChange={() => toggleContact(contact.id)}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                      selectedContacts.includes(contact.id) ? 'bg-[#CCFF00] border-[#CCFF00]' : 'border-white/30'
                    }`}>
                      {selectedContacts.includes(contact.id) && (
                        <svg className="w-3 h-3 text-black" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <div>
                      <span className="text-white">{contact.name}</span>
                      <span className="text-xs text-[#A1A1AA] ml-2">{contact.email}</span>
                    </div>
                  </label>
                ))}
              </div>
            ) : (
              <p className="text-[#A1A1AA] text-sm">Nessun contatto disponibile. <Link to="/dashboard/contacts" className="text-[#CCFF00]">Aggiungi un contatto</Link></p>
            )}
          </div>

          {/* Email Content */}
          <div className="space-y-3">
            <Label className="text-white">Oggetto email</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Es. Nuova musica dalla nostra band!"
              className="bg-[#0A0A0A] border-white/10"
              data-testid="promo-subject-input"
            />
          </div>

          <div className="space-y-3">
            <Label className="text-white">Messaggio (opzionale)</Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Scrivi un messaggio personalizzato..."
              rows={4}
              className="bg-[#0A0A0A] border-white/10 resize-none"
              data-testid="promo-message-input"
            />
          </div>

          <Button
            onClick={handleSendPromotion}
            disabled={sending || selectedTracks.length === 0 || selectedContacts.length === 0}
            className="w-full bg-[#CCFF00] text-black hover:bg-[#E5FF00]"
            data-testid="send-promo-btn"
          >
            {sending ? (
              <>
                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                Invio in corso...
              </>
            ) : (
              <>
                <PaperPlaneTilt size={20} className="mr-2" />
                Invia promozione ({selectedContacts.length} destinatari)
              </>
            )}
          </Button>
        </div>

        {/* Recent Promotions */}
        <div className="card rounded-xl p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Promozioni inviate</h2>
          {promotions.length > 0 ? (
            <div className="space-y-4">
              {promotions.map((promo, index) => (
                <motion.div
                  key={promo.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-4 bg-[#0A0A0A] rounded-lg"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-white">{promo.subject}</h3>
                    <span className={`badge ${promo.status === 'sent' ? 'badge-primary' : 'badge-secondary'}`}>
                      {promo.status === 'sent' ? 'Inviato' : 'Fallito'}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-[#A1A1AA]">
                    <span>{promo.track_ids.length} brani</span>
                    <span>{promo.emails_sent} email inviate</span>
                    <span>{new Date(promo.created_at).toLocaleDateString('it-IT')}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <PaperPlaneTilt size={48} className="text-[#333] mx-auto mb-4" />
              <p className="text-[#A1A1AA]">Nessuna promozione inviata</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Profile Page
const ProfilePage = () => {
  const { user, updateUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [genres, setGenres] = useState([]);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    genres: user?.genres || [],
    bio: user?.bio || '',
    location: user?.location || '',
    website: user?.website || '',
    spotify_url: user?.spotify_url || '',
    soundcloud_url: user?.soundcloud_url || '',
    instagram_url: user?.instagram_url || '',
  });

  useEffect(() => {
    fetchGenres();
  }, []);

  const fetchGenres = async () => {
    try {
      const response = await axios.get(`${API}/genres`);
      setGenres(response.data);
    } catch (error) {
      console.error('Failed to fetch genres:', error);
    }
  };

  const toggleGenre = (genre) => {
    setFormData(prev => {
      const current = prev.genres || [];
      if (current.includes(genre)) {
        return { ...prev, genres: current.filter(g => g !== genre) };
      } else if (current.length < 3) {
        return { ...prev, genres: [...current, genre] };
      }
      toast.error('Massimo 3 generi selezionabili');
      return prev;
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (formData.genres.length === 0) {
      toast.error('Seleziona almeno un genere');
      return;
    }
    setSaving(true);
    try {
      await updateUser(formData);
      toast.success('Profilo aggiornato!');
    } catch (error) {
      toast.error('Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  // Group genres by category
  const genreGroups = {
    'Rock & Derivati': genres.filter(g => ['Rock', 'Hard Rock', 'Alternative', 'Indie', 'Grunge'].includes(g)),
    'Metal': genres.filter(g => ['Metal', 'Death Metal', 'Thrash Metal', 'Trash-core', 'Grind-core'].includes(g)),
    'Punk': genres.filter(g => ['Punk', 'Punk HC', 'Crust', 'Tekno-punk'].includes(g)),
    'Elettronica': genres.filter(g => ['Electronic', 'House', 'Techno', 'Tekno', 'Frenchcore', 'Drum & Bass', 'Dubstep', 'Trance'].includes(g)),
    'Classici': genres.filter(g => ['Pop', 'Hip Hop', 'R&B', 'Jazz', 'Blues', 'Soul', 'Funk'].includes(g)),
    'Altri': genres.filter(g => ['Reggae', 'Ska', 'Country', 'Folk', 'Classical', 'Latin', 'World', 'Experimental'].includes(g)),
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Profilo</h1>
        <p className="text-[#A1A1AA]">Modifica le informazioni del tuo profilo pubblico</p>
      </div>

      <form onSubmit={handleSave} className="card rounded-xl p-6 max-w-2xl space-y-6">
        <div className="space-y-2">
          <Label className="text-white">Nome artista / Band</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="bg-[#0A0A0A] border-white/10"
            data-testid="profile-name-input"
          />
        </div>

        {/* Multi-genre selection */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-white">Generi musicali (max 3)</Label>
            <span className="text-xs text-[#A1A1AA]">{formData.genres.length}/3</span>
          </div>
          
          {/* Selected genres */}
          {formData.genres.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {formData.genres.map(genre => (
                <span
                  key={genre}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-[#CCFF00] text-black text-sm rounded-full font-medium"
                >
                  {genre}
                  <button type="button" onClick={() => toggleGenre(genre)}>
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>
          )}
          
          {/* Genre groups */}
          <div className="max-h-64 overflow-y-auto space-y-4 p-3 bg-[#0A0A0A] rounded-lg border border-white/10">
            {Object.entries(genreGroups).map(([groupName, groupGenres]) => (
              groupGenres.length > 0 && (
                <div key={groupName}>
                  <p className="text-xs text-[#666] uppercase tracking-wider mb-2">{groupName}</p>
                  <div className="flex flex-wrap gap-2">
                    {groupGenres.map(genre => (
                      <button
                        key={genre}
                        type="button"
                        onClick={() => toggleGenre(genre)}
                        className={`px-3 py-1 text-sm rounded-full border transition-all ${
                          formData.genres.includes(genre)
                            ? 'bg-[#CCFF00] text-black border-[#CCFF00]'
                            : formData.genres.length >= 3
                            ? 'bg-transparent text-[#666] border-[#333] cursor-not-allowed'
                            : 'bg-transparent text-white border-white/20 hover:border-[#CCFF00]'
                        }`}
                      >
                        {genre}
                      </button>
                    ))}
                  </div>
                </div>
              )
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-white">Bio</Label>
          <Textarea
            value={formData.bio}
            onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
            placeholder="Raccontaci di te e della tua musica..."
            rows={4}
            className="bg-[#0A0A0A] border-white/10 resize-none"
            data-testid="profile-bio-input"
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label className="text-white">Città</Label>
            <Input
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="Es. Milano"
              className="bg-[#0A0A0A] border-white/10"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-white">Sito web</Label>
            <Input
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              placeholder="https://..."
              className="bg-[#0A0A0A] border-white/10"
            />
          </div>
        </div>

        <div className="space-y-4">
          <Label className="text-white">Link social</Label>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <SpotifyLogo size={20} className="text-[#1DB954]" />
              <Input
                value={formData.spotify_url}
                onChange={(e) => setFormData({ ...formData, spotify_url: e.target.value })}
                placeholder="Link Spotify"
                className="bg-[#0A0A0A] border-white/10"
              />
            </div>
            <div className="flex items-center gap-3">
              <SoundcloudLogo size={20} className="text-[#FF5500]" />
              <Input
                value={formData.soundcloud_url}
                onChange={(e) => setFormData({ ...formData, soundcloud_url: e.target.value })}
                placeholder="Link SoundCloud"
                className="bg-[#0A0A0A] border-white/10"
              />
            </div>
          </div>
        </div>

        <Button
          type="submit"
          disabled={saving}
          className="bg-[#CCFF00] text-black hover:bg-[#E5FF00]"
          data-testid="save-profile-btn"
        >
          {saving ? 'Salvataggio...' : 'Salva modifiche'}
        </Button>
      </form>
    </div>
  );
};

// Main Dashboard Component
export default function MusicianDashboard() {
  return (
    <div className="min-h-screen bg-[#050505]">
      <Sidebar />
      <main className="ml-64 p-8 pb-32" data-testid="musician-dashboard">
        <Routes>
          <Route index element={<DashboardOverview />} />
          <Route path="tracks" element={<TracksPage />} />
          <Route path="contacts" element={<ContactsPage />} />
          <Route path="promotions" element={<PromotionsPage />} />
          <Route path="profile" element={<ProfilePage />} />
        </Routes>
      </main>
    </div>
  );
}
